import { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Heart, Mail, Instagram, Facebook, Twitter, Linkedin } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { LOGO_URL, FLAGS_URL } from "@/lib/brand";
import { toast } from "sonner";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/impact", label: "Community Impact" },
  { to: "/volunteer", label: "Volunteer" },
  { to: "/donate", label: "Donate" },
  { to: "/contact", label: "Contact" },
];

function AnnouncementBar() {
  return (
    <div className="bg-[#0A2540] text-white" data-testid="announcement-bar">
      <div className="container-app flex items-center justify-center gap-3 sm:gap-4 py-2 text-[11px] sm:text-xs">
        <img
          src={FLAGS_URL}
          alt="Aboriginal, Torres Strait Islander, Progress Pride, Transgender, and Non-binary flags"
          className="h-3.5 sm:h-4 w-auto object-contain"
          loading="lazy"
        />
        <span className="text-slate-200 hidden sm:inline">
          All are welcome here. We acknowledge the First Peoples and stand with LGBTQIA+ communities.
        </span>
        <span className="text-slate-200 sm:hidden">All are welcome here.</span>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white ring-1 ring-slate-100 shadow-sm overflow-hidden transition-transform duration-300 group-hover:-rotate-3">
        <img
          src={LOGO_URL}
          alt="CLEAN Water Drop Association logo"
          className="h-10 w-10 object-contain"
        />
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-heading text-base font-semibold tracking-tight text-[#0A2540]">
          CLEAN
        </span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
          Water Drop
        </span>
      </span>
    </Link>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-100 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="container-app flex h-20 items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV_LINKS.filter((l) => l.label !== "Donate").map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[#0A2540]" : "text-slate-600 hover:text-[#0A2540]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-1/2 -bottom-0.5 h-1 w-1 -translate-x-1/2 rounded-full bg-[#2E8B8B]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/donate"
            data-testid="nav-donate-button"
            className="btn-primary group hidden sm:inline-flex"
          >
            <Heart className="h-4 w-4 transition-transform group-hover:scale-110" />
            Donate Now
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white menu-anim" data-testid="mobile-menu">
          <div className="container-app py-6 flex flex-col gap-1">
            <div className="flex items-center justify-center pb-4">
              <img src={LOGO_URL} alt="CLEAN Water Drop" className="h-14 w-14 object-contain" />
            </div>
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-2xl px-5 py-3 text-base font-medium transition-colors ${
                    isActive ? "bg-[#E6F0F2] text-[#0A2540]" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/donate"
              data-testid="mobile-donate-button"
              className="btn-primary mt-3 w-full justify-center"
            >
              <Heart className="h-4 w-4" /> Donate Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're subscribed. Welcome to the circle.");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#0A2540] text-slate-200 mt-20" data-testid="site-footer">
      <div className="container-app py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white shadow overflow-hidden">
              <img src={LOGO_URL} alt="CLEAN Water Drop logo" className="h-12 w-12 object-contain" />
            </span>
            <div className="leading-none">
              <p className="font-heading text-lg font-semibold text-white">CLEAN Water Drop</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">Association</p>
            </div>
          </div>
          <p className="mt-6 text-base text-slate-300 leading-relaxed max-w-md">
            A community-led nonprofit dedicated to clean water access, dignified care,
            and trauma-informed support for women, youth, and those without a home.
          </p>

          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Stay in the loop — your email"
              data-testid="newsletter-email-input"
              className="flex-1 rounded-full bg-white/10 border border-white/10 px-5 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2E8B8B]"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-button"
              className="rounded-full bg-[#2E8B8B] px-6 py-3 text-sm font-medium text-white hover:bg-[#3AABAB] transition-colors disabled:opacity-70"
            >
              {loading ? "Joining…" : "Subscribe"}
            </button>
          </form>

          <div className="mt-8 flex items-center gap-3">
            <img src={FLAGS_URL} alt="Community flags" className="h-5 w-auto object-contain opacity-90" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400">All are welcome here</span>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Explore</p>
          <ul className="mt-5 space-y-3 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-slate-300 hover:text-white transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Reach Us</p>
          <ul className="mt-5 space-y-4 text-sm text-slate-300">
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 mt-0.5 text-[#2E8B8B]" />
              <a href="mailto:Info.cleanwaterdrop@gmail.com" className="hover:text-white break-all">
                Info.cleanwaterdrop@gmail.com
              </a>
            </li>
            <li className="text-xs text-slate-400 leading-relaxed">
              We reply within 48 hours, Monday to Friday. For urgent matters, please call your
              local emergency services first.
            </li>
          </ul>
          <div className="mt-6 flex items-center gap-2.5">
            {[Instagram, Facebook, Twitter, Linkedin].map((Icon, i) => (
              <a
                key={i}
                href="#"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-slate-300 hover:border-[#2E8B8B] hover:text-white transition-colors"
                aria-label="social"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-app py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <p>© {new Date().getFullYear()} CLEAN Water Drop Association. A community-led nonprofit.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">Terms</Link>
            <span className="flex items-center gap-2">
              Built with <Heart className="h-3.5 w-3.5 text-[#2E8B8B]" /> for the communities we serve.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <a href="#main-content" className="skip-link">Skip to content</a>
      <Navbar />
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
